import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RecordsTable, type Column, type FilterDef } from '../components/RecordsTable/RecordsTable';
import { mcpCall } from '../lib/mcp';
import './Settings.css';
import './Privacy.css';

/**
 * Cancelled subscriptions (2026-09-26). Cancelling stops the automatic reorder; nothing is
 * refunded, and a box already paid for still ships (CompanyData cancellation-policy). "Paid box in
 * flight" says whether that was the case at the moment of cancelling.
 *
 * No household id and no personal data: the console shows no subscriber details anywhere else,
 * and this page does not start.
 */

type How = 'online' | 'deletion' | 'other';

interface CancellationRow {
  id: number;
  public_id: string;
  cancelled_at: string;
  cancel_reason: string | null;
  how: How;
  filters_per_shipment: number;
  cadence_months: number;
  started_at: string | null;
  paid_box_in_flight: boolean;
}

interface Counts {
  total: number;
  online: number;
  deletion: number;
  other: number;
}

const HOW_LABEL: Record<How, string> = { online: 'Online', deletion: 'Delete-my-data', other: 'Other' };
const when = (v: string | null) => (v ? new Date(v).toLocaleString() : '—');
const day = (v: string | null) => (v ? new Date(v).toLocaleDateString() : '—');
const DATE = /^\d{4}-\d{2}-\d{2}$/;

const COLUMNS: Column<CancellationRow>[] = [
  { key: 'cancelled_at', header: 'Cancelled', sortable: true, render: (r) => when(r.cancelled_at) },
  { key: 'public_id', header: 'Subscription', render: (r) => <code className="privacy__id">{r.public_id}</code> },
  { key: 'how', header: 'How', render: (r) => <span className="records__tag">{HOW_LABEL[r.how] ?? r.how}</span> },
  { key: 'cancel_reason', header: 'Reason', render: (r) => r.cancel_reason ?? '—' },
  { key: 'filters_per_shipment', header: 'Filters per box', numeric: true, sortable: true },
  { key: 'cadence_months', header: 'Every', numeric: true, sortable: true, render: (r) => `${r.cadence_months} mo` },
  { key: 'started_at', header: 'Started', sortable: true, sortAccessor: (r) => r.started_at, render: (r) => day(r.started_at) },
  {
    key: 'paid_box_in_flight',
    header: 'Paid box in flight',
    render: (r) =>
      r.paid_box_in_flight ? <span className="records__tag records__tag--warn">Yes, still ships</span> : <span className="records__tag">No</span>,
  },
];

const FILTERS: FilterDef<CancellationRow>[] = [
  {
    key: 'how',
    label: 'How',
    options: [
      { value: 'all', label: 'All' },
      { value: 'online', label: 'Online' },
      { value: 'deletion', label: 'Delete-my-data' },
      { value: 'other', label: 'Other' },
    ],
    match: (r, v) => r.how === v,
  },
];

export function Cancellations() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const inverted = DATE.test(from) && DATE.test(to) && from > to;
  // Only complete dates reach the MCP, and never an inverted range: it would be refused.
  const range: Record<string, string> = {};
  if (!inverted && DATE.test(from)) range.from = from;
  if (!inverted && DATE.test(to)) range.to = to;

  const counts = useQuery({
    queryKey: ['mcp', 'listCancellations', 'counts', range],
    queryFn: async () => (await mcpCall<{ counts: Counts }>('listCancellations', { ...range, limit: 1 })).counts,
  });

  return (
    <>
      <div className="settings__list">
        <div className="settings__row">
          <div className="settings__meta">
            <p className="settings__key" id="range-title">Date range</p>
            <p className="settings__desc">By the day the subscription was cancelled (UTC). Leave both empty for all time.</p>
            {inverted && <p className="privacy__error" role="alert">The start date is after the end date.</p>}
          </div>
          <div className="privacy__range" role="group" aria-labelledby="range-title">
            <label className="privacy__field">
              From
              <input className="settings__input" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="privacy__field">
              To
              <input className="settings__input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button
              type="button"
              className="records__refresh"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              disabled={!from && !to}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
      {counts.data && (
        <dl className="privacy__counts" aria-label="Cancellations in this range">
          <div className="privacy__count"><dt>Total</dt><dd>{counts.data.total}</dd></div>
          <div className="privacy__count"><dt>Online</dt><dd>{counts.data.online}</dd></div>
          <div className="privacy__count"><dt>Via delete-my-data</dt><dd>{counts.data.deletion}</dd></div>
          <div className="privacy__count"><dt>Other</dt><dd>{counts.data.other}</dd></div>
        </dl>
      )}
      <RecordsTable<CancellationRow>
        title="Cancellations"
        intro="Cancelled subscriptions, newest first. Cancelling stops the automatic reorder; nothing is refunded, and a box already paid for still ships."
        tool="listCancellations"
        args={{ ...range, limit: 500 }}
        select={(r) => (r as { cancellations: CancellationRow[] }).cancellations ?? []}
        columns={COLUMNS}
        rowKey={(r) => r.id}
        emptyMessage="No cancellations in this range."
        refreshable
        filters={FILTERS}
        defaultSort={{ key: 'cancelled_at', dir: 'desc' }}
      />
    </>
  );
}
